import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n/index";

const LANGUAGES = [
  { code: "en", label: "EN", flag: "🇬🇧" },
  { code: "ru", label: "RU", flag: "🇷🇺" },
  { code: "it", label: "IT", flag: "🇮🇹" },
  { code: "bg", label: "BG", flag: "🇧🇬" },
];

export default function Header() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState("en");
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLangChange = (code: string) => {
    setCurrentLang(code);
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const activeLang = LANGUAGES.find((l) => l.code === currentLang) || LANGUAGES[0];

  const navLinks = [
    { href: "#home", key: "nav_home" },
    { href: "#about", key: "nav_about" },
    { href: "#pension-options", key: "nav_plans" },
    { href: "#investment-slider", key: "nav_calculator" },
    { href: "#benefits", key: "nav_benefits" },
    { href: "#contact", key: "nav_contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-18 md:h-20">
        <a href="#home" className="flex items-center gap-2.5 cursor-pointer">
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-emerald-700">
            <i className="ri-shield-star-line text-white text-base"></i>
          </div>
          <div>
            <div className="font-bold text-xs leading-tight tracking-wide text-gray-900">GOVERNMENT PENSION</div>
            <div className="text-xs tracking-widest text-emerald-700">FUND GLOBAL</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-gray-700 hover:text-emerald-700"
            >
              {t(link.key)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Language Switcher */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full border transition-colors cursor-pointer whitespace-nowrap border-gray-200 text-gray-700 hover:border-emerald-400"
            >
              <span>{activeLang.flag}</span>
              <span className="hidden sm:inline">{activeLang.label}</span>
              <i className="ri-arrow-down-s-line text-xs"></i>
            </button>
            {langOpen && (
              <div className="absolute right-0 top-full mt-2 bg-white border border-gray-100 rounded-xl overflow-hidden w-36 z-50">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLangChange(lang.code)}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors cursor-pointer text-left ${
                      currentLang === lang.code
                        ? "bg-emerald-50 text-emerald-700 font-semibold"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                    {currentLang === lang.code && (
                      <i className="ri-check-line ml-auto text-emerald-600 text-xs"></i>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="#contact"
            className="hidden md:block bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-emerald-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("nav_open_account")}
          </a>
          <button
            className="lg:hidden w-9 h-9 flex items-center justify-center text-gray-800 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-4 py-4 flex flex-col gap-3">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="text-sm font-medium text-gray-700 hover:text-emerald-700 py-1 cursor-pointer"
            >
              {t(link.key)}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setMobileOpen(false)}
            className="bg-emerald-700 text-white text-sm font-semibold px-4 py-2.5 rounded-full text-center mt-1 cursor-pointer"
          >
            {t("nav_open_account")}
          </a>
        </div>
      )}
    </header>
  );
}
